/**
 * ShotgunComponent - projectile weapon with multi-pellet spread and reload animation.
 * Extends ProjectileWeaponComponent with shotgun-specific behavior.
 */

import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';

import { ProjectileWeaponComponent } from './ProjectileWeaponComponent.js';

import type { ProjectileWeaponOptions } from './ProjectileWeaponComponent.js';

// ShotgunComponent extends ProjectileWeaponComponent with shotgun-specific behavior
@EngineClass('ShotgunComponent')
export class ShotgunComponent extends ProjectileWeaponComponent {


  private originalRotation: THREE.Euler = new THREE.Euler(0, 0, 0);
  private reloadRotation: THREE.Euler = new THREE.Euler(Math.PI / 2, Math.PI / 2, 0);
  private isRotating: boolean = false;

  public override initialize(options?: ProjectileWeaponOptions): void {
    // Apply shotgun defaults, then user overrides
    const shotgunDefaults: ProjectileWeaponOptions = {
      fireInterval: 1.0 / 1.5,
      isSingleFire: true,
      modelPath: '@engine/assets/models/Weapons/SM_ShotGun_01.glb',
      modelTransform: {
        position: new THREE.Vector3(0.3, -0.3, -0.4),
        rotation: new THREE.Euler(0, Math.PI / 2, 0),
      },
      projectileSpawnOffset: new THREE.Vector3(0.6, -0.5, -1.5),
      projectileOptions: {
        velocity: 30,
        range: 50,
        damageDealt: 25,
        useOverlapCheck: true,
        destroyOnHit: true,
        gravityScale: 0.2,
        geometry: new THREE.SphereGeometry(0.15),
        material: new THREE.MeshStandardMaterial({
          color: 0xFFD700,
          emissive: 0x444400,
          emissiveIntensity: 0.2
        }),
      },
      ammoCapacity: 8,
      reloadTime: 2.5,
    };
    super.initialize({ ...shotgunDefaults, ...options });
  }

  // Provide access to weapon mesh for compatibility
  protected get gunMesh(): THREE.Object3D | null {
    return this.weaponMesh;
  }

  // Override reload to add rotation animation
  protected override startReload(): void {
    if (this.ammoCapacity <= 0 || this.isReloading) return;

    this.isReloading = true;
    this.isRotating = true;
    console.log('Reloading shotgun with animation...');

    // Start rotation animation to point up
    this.animateRotationTo(this.reloadRotation, 0.5);

    // Use timer system for reload
    const world = this.getWorld();
    if (world) {
      world.timerSystem.setTimeout(() => {
        this.currentAmmo = this.ammoCapacity;
        this.isReloading = false;

        // Start rotation animation back to normal
        this.animateRotationTo(this.originalRotation, 0.5, () => {
          this.isRotating = false;
          console.log('Shotgun reload complete');
        });
      }, this.reloadTime);
    }
  }

  // Smooth rotation animation helper
  private animateRotationTo(targetRotation: THREE.Euler, duration: number, onComplete?: () => void): void {
    if (!this.gunMesh) return;

    // Convert current and target rotations to quaternions for proper interpolation
    const startQuaternion = new THREE.Quaternion().setFromEuler(this.gunMesh.rotation);
    const targetQuaternion = new THREE.Quaternion().setFromEuler(targetRotation);

    const startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);

      // Use smooth easing function
      const easedProgress = this.easeInOutCubic(progress);

      if (this.gunMesh) {
        // Use spherical linear interpolation (slerp) for smooth rotation
        const currentQuaternion = new THREE.Quaternion();
        currentQuaternion.slerpQuaternions(startQuaternion, targetQuaternion, easedProgress);

        // Apply the interpolated quaternion to the mesh
        this.gunMesh.setRotationFromQuaternion(currentQuaternion);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - ensure exact target rotation
        if (this.gunMesh) {
          this.gunMesh.rotation.copy(targetRotation);
        }
        if (onComplete) {
          onComplete();
        }
      }
    };

    animate();
  }

  // Smooth easing function
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Override beginFire to prevent firing during rotation
  public override beginFire(): boolean {
    if (this.isRotating) {
      return false; // Silent fail during reload animation
    }

    return super.beginFire();
  }

  // Override doFire to add muzzle flash effects
  protected override doFire(): void {
    super.doFire();

    void this.spawnMuzzleFlash();
  }

  private async spawnMuzzleFlash(): Promise<void> {
    const world = this.getWorld();
    if (!world) return;

    const { position, direction } = this.getFirePositionAndDirection();

    // Calculate rotation to align with fire direction
    const lookDirection = direction.clone().normalize();
    const defaultForward = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(defaultForward, lookDirection);
    const euler = new THREE.Euler().setFromQuaternion(quaternion);

    // Spawn muzzle flash effect
    await world.globalParticleManager.spawnVFX(
      'shotgunMuzzleFlash',
      {
        nbParticles: 200,
        intensity: 15,
        renderMode: 'stretchBillboard',
        appearance: 'square',
        fadeAlpha: [0.5, 0],
        fadeSize: [0.05, 0.15],
        blendingMode: 'additive',
        easeFunction: 'easeOutQuad',
      },
      {
        loop: false,
        duration: 0.03,
        nbParticles: 25,
        spawnMode: 'burst',
        particlesLifetime: [0.04, 0.08],
        startPositionMin: [0, 0, 0],
        startPositionMax: [0, 0, 0],
        // Forward cone spread
        directionMin: [-0.15, 0.9, -0.15],
        directionMax: [0.15, 1.1, 0.15],
        size: [0.02, 0.06],
        speed: [20, 35],
        colorStart: ['#ffff00', '#ffcc00', '#ff8800'], // Bright yellow-orange
        colorEnd: ['#ff4400', '#882200', '#440000'],  // Fade to darker orange
        useLocalDirection: true,
      },
      {
        position: position.clone(),
        rotation: euler,
      }
    );
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Shotgun';
  }
}
